import { useState } from 'react'
import {
  Star,
  Users,
  Shield,
  Server,
  Zap,
  ChevronDown,
  MessageSquare,
  ThumbsUp,
  ThumbsDown,
  Share2,
  Heart,
  Bookmark,
  ArrowRight,
  CheckCircle,
  AlertTriangle,
  Info,
} from 'lucide-react'
import Link from 'next/link'
import { useRouter } from 'next/router'
import { mcpServers, sampleReviews } from 'constants/agents'
import { Button } from '@components/ui/button'

export default function MCPDetailPage() {
  const router = useRouter()
  const { slug } = router.query

  // Find the MCP by ID
  const mcp = mcpServers.find((m) => m.id === slug)

  // State for reviews
  const [reviewSort, setReviewSort] = useState('helpful')
  const [showAllReviews, setShowAllReviews] = useState(false)

  // State for usage
  const [selectedPlan, setSelectedPlan] = useState('free')

  // If MCP not found
  if (!mcp) {
    return (
      <div className="min-h-screen bg-theme-background">
        <div className="max-w-screen-xl mx-auto px-4 py-16 text-center">
          <h1 className="text-3xl font-bold mb-4">MCP Server Not Found</h1>
          <p className="text-theme-text-muted mb-8">
            The MCP server you&apos;re looking for doesn&apos;t exist or has been removed.
          </p>
          <Link href="/agents">
            <Button size="lg" className="mb-2 w-full md:w-auto">
              Return to Directory
            </Button>
          </Link>
        </div>
      </div>
    )
  }

  // Sort reviews based on selected option
  const sortedReviews = [...sampleReviews].sort((a, b) => {
    switch (reviewSort) {
      case 'recent':
        return new Date(b.date).getTime() - new Date(a.date).getTime()
      case 'helpful':
        return b.likes - a.likes
      case 'highest':
        return b.rating - a.rating
      case 'lowest':
        return a.rating - b.rating
      default:
        return 0
    }
  })

  // Display limited reviews or all
  const displayedReviews = showAllReviews ? sortedReviews : sortedReviews.slice(0, 3)

  // Render status badge with appropriate color
  const renderStatusBadge = (status) => {
    let bgColor = ''
    switch (status) {
      case 'Online':
        bgColor = 'bg-green-500'
        break
      case 'Maintenance':
        bgColor = 'bg-yellow-500'
        break
      case 'Beta':
        bgColor = 'bg-purple-500'
        break
      case 'New':
        bgColor = 'bg-blue-500'
        break
    }

    return <span className={`${bgColor} text-white text-xs px-2 py-1 rounded-full`}>{status}</span>
  }

  return (
    <div className="min-h-screen bg-theme-background">
      {/* Hero Section */}
      <section className="bg-gradient-to-r from-gray-900 to-gray-800 text-white py-16 px-4">
        <div className="max-w-screen-xl mx-auto">
          <div className="flex flex-col md:flex-row items-start gap-8">
            <div className="w-24 h-24 rounded-lg overflow-hidden bg-white p-2 flex-shrink-0">
              <img src={mcp.image || '/placeholder.svg'} alt={mcp.name} className="w-full h-full object-cover" />
            </div>

            <div className="flex-1">
              <div className="flex items-center gap-3 mb-2">
                <h1 className="text-4xl font-bold">{mcp.name}</h1>
                {renderStatusBadge(mcp.status)}
              </div>

              <p className="text-xl text-gray-300 mb-4">{mcp.description}</p>

              <div className="flex flex-wrap items-center gap-6">
                <div className="flex items-center">
                  <Star className="text-yellow-400 mr-1" size={20} />
                  <span className="font-bold mr-1">{mcp.rating}</span>
                  <span className="text-gray-300">({mcp.reviews} reviews)</span>
                </div>

                <div className="flex items-center">
                  <Users className="text-gray-300 mr-1" size={18} />
                  <span>{mcp.users.toLocaleString()} active users</span>
                </div>

                <div className="flex items-center">
                  <Shield className="text-gray-300 mr-1" size={18} />
                  <span>By {mcp.company.name}</span>
                  {mcp.company.verified && <CheckCircle className="ml-1 text-blue-400" size={16} />}
                </div>
              </div>
            </div>

            <div className="mt-6 md:mt-0">
              <Button variant="gradient" size="lg" className="mb-2 w-full md:w-auto">
                Connect Now
              </Button>
              <div className="flex gap-2 mt-3">
                <button className="p-2 rounded-full bg-gray-800 hover:bg-gray-700 transition-colors">
                  <Heart size={18} />
                </button>
                <button className="p-2 rounded-full bg-gray-800 hover:bg-gray-700 transition-colors">
                  <Bookmark size={18} />
                </button>
                <button className="p-2 rounded-full bg-gray-800 hover:bg-gray-700 transition-colors">
                  <Share2 size={18} />
                </button>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* Main Content */}
      <section className="py-12 px-4">
        <div className="max-w-screen-xl mx-auto">
          <div className="grid grid-cols-1 lg:grid-cols-3 gap-8">
            {/* Left Column - Details */}
            <div className="lg:col-span-2">
              {/* About */}
              <div className="mb-10">
                <h2 className="text-2xl font-bold mb-4 text-theme-text">About {mcp.name}</h2>
                <p className="text-theme-text-muted mb-6">{mcp.longDescription}</p>

                <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                  {/* Features */}
                  <div className="border border-theme-border rounded-lg p-6">
                    <h3 className="font-bold text-lg mb-4 flex items-center text-theme-text">
                      <Zap className="mr-2 text-yellow-500" size={18} />
                      Key Features
                    </h3>
                    <ul className="space-y-2">
                      {mcp.features.map((feature, index) => (
                        <li key={index} className="flex items-start">
                          <CheckCircle className="text-green-500 mr-2 flex-shrink-0 mt-1" size={16} />
                          <span className="text-theme-text-muted">{feature}</span>
                        </li>
                      ))}
                    </ul>
                  </div>

                  {/* Specs */}
                  <div className="border border-theme-border rounded-lg p-6">
                    <h3 className="font-bold text-lg mb-4 flex items-center text-theme-text">
                      <Server className="mr-2 text-blue-500" size={18} />
                      Technical Specifications
                    </h3>
                    <div className="space-y-4">
                      <div>
                        <div className="text-sm text-theme-text-muted mb-1">Memory</div>
                        <div className="font-medium text-theme-text">{mcp.specs.memory}</div>
                      </div>
                      <div>
                        <div className="text-sm text-theme-text-muted mb-1">Processing Power</div>
                        <div className="font-medium text-theme-text">{mcp.specs.processing}</div>
                      </div>
                      <div>
                        <div className="text-sm text-theme-text-muted mb-1">Storage</div>
                        <div className="font-medium text-theme-text">{mcp.specs.storage}</div>
                      </div>
                      <div>
                        <div className="text-sm text-theme-text-muted mb-1">Uptime</div>
                        <div className="font-medium text-theme-text">{mcp.specs.uptime}</div>
                      </div>
                    </div>
                  </div>
                </div>
              </div>

              {/* Reviews */}
              <div className="mb-10">
                <div className="flex items-center justify-between mb-6">
                  <h2 className="text-2xl font-bold text-theme-text">User Reviews</h2>
                  <div className="flex items-center">
                    <span className="text-sm text-theme-text-muted mr-2">Sort by:</span>
                    <select
                      className="border border-theme-border rounded-md px-2 py-1 text-sm bg-theme-background"
                      value={reviewSort}
                      onChange={(e) => setReviewSort(e.target.value)}
                    >
                      <option value="helpful">Most Helpful</option>
                      <option value="recent">Most Recent</option>
                      <option value="highest">Highest Rated</option>
                      <option value="lowest">Lowest Rated</option>
                    </select>
                  </div>
                </div>

                {/* Review Summary */}
                <div className="flex flex-col md:flex-row gap-6 mb-8 p-6 border border-theme-border rounded-lg">
                  <div className="text-center">
                    <div className="text-5xl font-bold text-theme-text mb-2">{mcp.rating}</div>
                    <div className="flex justify-center mb-1">
                      {[...Array(5)].map((_, i) => (
                        <Star
                          key={i}
                          size={18}
                          className={i < Math.floor(mcp.rating) ? 'text-yellow-400' : 'text-gray-300'}
                          fill={i < Math.floor(mcp.rating) ? 'currentColor' : 'none'}
                        />
                      ))}
                    </div>
                    <div className="text-sm text-theme-text-muted">Based on {mcp.reviews} reviews</div>
                  </div>

                  <div className="flex-1 flex flex-col justify-center">
                    {/* Rating bars */}
                    <div className="space-y-2">
                      {[5, 4, 3, 2, 1].map((rating) => {
                        // Calculate percentage (mock data)
                        const percentage =
                          rating === 5 ? 70 : rating === 4 ? 20 : rating === 3 ? 7 : rating === 2 ? 2 : 1

                        return (
                          <div key={rating} className="flex items-center">
                            <div className="flex items-center w-12">
                              <span className="text-sm text-theme-text mr-1">{rating}</span>
                              <Star size={12} className="text-yellow-400" />
                            </div>
                            <div className="flex-1 h-2 bg-gray-200 rounded-full overflow-hidden">
                              <div
                                className="h-full bg-yellow-400 rounded-full"
                                style={{ width: `${percentage}%` }}
                              ></div>
                            </div>
                            <span className="text-sm text-theme-text-muted ml-2 w-10">{percentage}%</span>
                          </div>
                        )
                      })}
                    </div>
                  </div>

                  <div className="text-center flex flex-col justify-center">
                    <Button variant="primary" size="md" className="mb-2">
                      Write a Review
                    </Button>
                    <p className="text-xs text-theme-text-muted">Share your experience with this MCP</p>
                  </div>
                </div>

                {/* Review List */}
                <div className="space-y-6">
                  {displayedReviews.map((review) => (
                    <div key={review.id} className="border border-theme-border rounded-lg p-6">
                      <div className="flex justify-between mb-3">
                        <div className="flex items-center">
                          <div className="w-10 h-10 rounded-full overflow-hidden mr-3">
                            <img
                              src={review.user.image || '/placeholder.svg'}
                              alt={review.user.name}
                              className="w-full h-full object-cover"
                            />
                          </div>
                          <div>
                            <div className="flex items-center">
                              <span className="font-medium text-theme-text">{review.user.name}</span>
                              {review.user.verified && <CheckCircle className="ml-1 text-blue-500" size={14} />}
                            </div>
                            <div className="text-xs text-theme-text-muted">
                              {new Date(review.date).toLocaleDateString('en-US', {
                                year: 'numeric',
                                month: 'short',
                                day: 'numeric',
                              })}
                            </div>
                          </div>
                        </div>

                        <div className="flex items-center">
                          {[...Array(5)].map((_, i) => (
                            <Star
                              key={i}
                              size={16}
                              className={i < review.rating ? 'text-yellow-400' : 'text-gray-300'}
                              fill={i < review.rating ? 'currentColor' : 'none'}
                            />
                          ))}
                        </div>
                      </div>

                      <h4 className="font-bold text-theme-text mb-2">{review.title}</h4>
                      <p className="text-theme-text-muted mb-4">{review.content}</p>

                      <div className="flex items-center justify-between">
                        <div className="flex items-center space-x-4">
                          <button className="flex items-center text-theme-text-muted hover:text-theme-text">
                            <ThumbsUp size={16} className="mr-1" />
                            <span>{review.likes}</span>
                          </button>
                          <button className="flex items-center text-theme-text-muted hover:text-theme-text">
                            <ThumbsDown size={16} className="mr-1" />
                            <span>{review.dislikes}</span>
                          </button>
                          <button className="flex items-center text-theme-text-muted hover:text-theme-text">
                            <MessageSquare size={16} className="mr-1" />
                            <span>{review.replies} replies</span>
                          </button>
                        </div>

                        <button className="text-theme-primary text-sm hover:underline">Report</button>
                      </div>
                    </div>
                  ))}
                </div>

                {!showAllReviews && sampleReviews.length > 3 && (
                  <div className="text-center mt-6">
                    <button
                      className="text-theme-primary hover:underline flex items-center mx-auto"
                      onClick={() => setShowAllReviews(true)}
                    >
                      Show all {sampleReviews.length} reviews
                      <ChevronDown size={16} className="ml-1" />
                    </button>
                  </div>
                )}
              </div>
            </div>

            {/* Right Column - Usage and Info */}
            <div>
              {/* Usage Card */}
              <div className="border border-theme-border rounded-lg overflow-hidden sticky top-24">
                <div className="p-6">
                  <h3 className="font-bold text-xl mb-4 text-theme-text">Use {mcp.name}</h3>

                  {/* Plan Selection */}
                  <div className="mb-6">
                    <div className="flex border border-theme-border rounded-md overflow-hidden">
                      <button
                        className={`flex-1 py-2 text-center ${selectedPlan === 'free' ? 'bg-theme-primary text-white' : 'bg-theme-background text-theme-text'}`}
                        onClick={() => setSelectedPlan('free')}
                      >
                        Free
                      </button>
                      <button
                        className={`flex-1 py-2 text-center ${selectedPlan === 'pro' ? 'bg-theme-primary text-white' : 'bg-theme-background text-theme-text'}`}
                        onClick={() => setSelectedPlan('pro')}
                      >
                        Pro
                      </button>
                    </div>
                  </div>

                  {/* Plan Details */}
                  <div className="mb-6">
                    <div className="flex justify-between items-center mb-2">
                      <span className="font-medium text-theme-text">
                        {selectedPlan === 'free' ? 'Free Plan' : 'Pro Plan'}
                      </span>
                      <span className="font-bold text-xl text-theme-text">
                        {selectedPlan === 'free' ? '$0' : '$10'}/month
                      </span>
                    </div>

                    <ul className="space-y-2 mb-4">
                      {selectedPlan === 'free' ? (
                        <>
                          <li className="flex items-start">
                            <CheckCircle className="text-green-500 mr-2 flex-shrink-0 mt-1" size={16} />
                            <span className="text-theme-text-muted">Basic access to MCP capabilities</span>
                          </li>
                          <li className="flex items-start">
                            <CheckCircle className="text-green-500 mr-2 flex-shrink-0 mt-1" size={16} />
                            <span className="text-theme-text-muted">100 queries per day</span>
                          </li>
                          <li className="flex items-start">
                            <CheckCircle className="text-green-500 mr-2 flex-shrink-0 mt-1" size={16} />
                            <span className="text-theme-text-muted">Standard response time</span>
                          </li>
                          <li className="flex items-start">
                            <AlertTriangle className="text-yellow-500 mr-2 flex-shrink-0 mt-1" size={16} />
                            <span className="text-theme-text-muted">Limited memory retention</span>
                          </li>
                        </>
                      ) : (
                        <>
                          <li className="flex items-start">
                            <CheckCircle className="text-green-500 mr-2 flex-shrink-0 mt-1" size={16} />
                            <span className="text-theme-text-muted">Full access to all capabilities</span>
                          </li>
                          <li className="flex items-start">
                            <CheckCircle className="text-green-500 mr-2 flex-shrink-0 mt-1" size={16} />
                            <span className="text-theme-text-muted">Unlimited queries</span>
                          </li>
                          <li className="flex items-start">
                            <CheckCircle className="text-green-500 mr-2 flex-shrink-0 mt-1" size={16} />
                            <span className="text-theme-text-muted">Priority response time</span>
                          </li>
                          <li className="flex items-start">
                            <CheckCircle className="text-green-500 mr-2 flex-shrink-0 mt-1" size={16} />
                            <span className="text-theme-text-muted">Extended memory retention</span>
                          </li>
                          <li className="flex items-start">
                            <CheckCircle className="text-green-500 mr-2 flex-shrink-0 mt-1" size={16} />
                            <span className="text-theme-text-muted">API access</span>
                          </li>
                        </>
                      )}
                    </ul>
                  </div>

                  {/* Action Buttons */}
                  <div className="space-y-3">
                    <Button variant="gradient" size="lg" className="w-full">
                      {selectedPlan === 'free' ? 'Start Free' : 'Upgrade to Pro'}
                    </Button>

                    {selectedPlan === 'pro' && (
                      <p className="text-xs text-center text-theme-text-muted">7-day free trial, cancel anytime</p>
                    )}
                  </div>
                </div>

                {/* Company Info */}
                <div className="border-t border-theme-border p-6">
                  <h4 className="font-medium text-theme-text mb-3">About the Provider</h4>

                  <div className="space-y-2 text-sm">
                    <div className="flex justify-between">
                      <span className="text-theme-text-muted">Company</span>
                      <span className="text-theme-text font-medium flex items-center">
                        {mcp.company.name}
                        {mcp.company.verified && <CheckCircle className="ml-1 text-blue-500" size={14} />}
                      </span>
                    </div>
                    <div className="flex justify-between">
                      <span className="text-theme-text-muted">Founded</span>
                      <span className="text-theme-text">{mcp.company.founded}</span>
                    </div>
                    <div className="flex justify-between">
                      <span className="text-theme-text-muted">Website</span>
                      <a
                        href={mcp.company.website}
                        className="text-theme-primary hover:underline"
                        target="_blank"
                        rel="noopener noreferrer"
                      >
                        Visit site
                      </a>
                    </div>
                  </div>

                  <div className="mt-4 pt-4 border-t border-theme-border">
                    <div className="flex items-center justify-between">
                      <div className="flex items-center">
                        <Info size={16} className="text-theme-text-muted mr-1" />
                        <span className="text-xs text-theme-text-muted">Report an issue</span>
                      </div>
                      <Link href="/agents/support" className="text-xs text-theme-primary hover:underline">
                        Contact support
                      </Link>
                    </div>
                  </div>
                </div>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* Related MCPs */}
      <section className="py-12 px-4 bg-theme-secondary">
        <div className="max-w-screen-xl mx-auto">
          <h2 className="text-2xl font-bold mb-8 text-theme-text">Similar MCP Servers</h2>

          <div className="grid grid-cols-1 md:grid-cols-3 gap-6">
            {mcpServers
              .filter((m) => m.id !== mcp.id && m.category === mcp.category)
              .slice(0, 3)
              .map((relatedMcp) => (
                <Link key={relatedMcp.id} href={`/agents/${relatedMcp.id}`}>
                  <div className="border border-theme-border bg-theme-background rounded-lg overflow-hidden hover:shadow-md transition-shadow hover:border-theme-primary">
                    <div className="p-4">
                      <div className="flex items-center mb-3">
                        <div className="w-10 h-10 rounded-md overflow-hidden mr-3">
                          <img
                            src={relatedMcp.image || '/placeholder.svg'}
                            alt={relatedMcp.name}
                            className="w-full h-full object-cover"
                          />
                        </div>
                        <div>
                          <h3 className="font-bold text-theme-text">{relatedMcp.name}</h3>
                          <div className="flex items-center">
                            <Star size={14} className="text-yellow-400 mr-1" />
                            <span className="text-sm text-theme-text-muted">
                              {relatedMcp.rating} ({relatedMcp.reviews})
                            </span>
                          </div>
                        </div>
                      </div>

                      <p className="text-sm text-theme-text-muted mb-3 line-clamp-2">{relatedMcp.description}</p>

                      <div className="flex justify-between items-center">
                        <span className="text-sm font-medium text-theme-text">{relatedMcp.price}</span>
                        <span className="text-xs bg-theme-secondary text-theme-text-muted px-2 py-1 rounded-full">
                          {relatedMcp.category}
                        </span>
                      </div>
                    </div>
                  </div>
                </Link>
              ))}
          </div>

          <div className="text-center mt-8">
            <Link href="/agents">
              <Button variant="outline" size="md">
                View All MCP Servers
                <ArrowRight size={16} className="ml-2" />
              </Button>
            </Link>
          </div>
        </div>
      </section>
    </div>
  )
}
